import { useState, useEffect, useRef, type FormEvent } from 'react';
import { createPortal } from 'react-dom';
import { useCreateTag, TAG_COLOR_PALETTE, DEFAULT_TAG_COLOR } from '../../features/tags';
import { brandConfig } from '../../config/brands';
import styles from './CreateTagModal.module.css';
import '../../styles/buttons.css';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onCreated?: (tagName: string) => void;
  position?: { top: number; left: number };
};

export function CreateTagModal({ isOpen, onClose, onCreated, position }: Props) {
  const [name, setName] = useState('');
  const [color, setColor] = useState<string>(DEFAULT_TAG_COLOR);
  const [error, setError] = useState<string | null>(null);
  const createTagMutation = useCreateTag();
  const modalRef = useRef<HTMLDivElement>(null);

  // モーダルが開いたときに入力欄をクリア
  useEffect(() => {
    if (isOpen) {
      setName('');
      setColor(DEFAULT_TAG_COLOR);
      setError(null);
    }
  }, [isOpen]);

  // 外側クリックで閉じる (position 指定時のポップオーバー用。オーバーレイモードでは overlay の onClick で閉じる)
  useEffect(() => {
    if (!position) return;
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (modalRef.current && !modalRef.current.contains(target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, onClose, position]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;

    setError(null);

    try {
      await createTagMutation.mutateAsync({ name: trimmed, color });
      onCreated?.(trimmed);
      onClose();
    } catch (err: any) {
      console.error('Failed to create tag:', err);
      const errorMessage = err?.response?.data?.error || err?.message || 'タグの作成に失敗しました';
      setError(errorMessage);
    }
  };

  if (!isOpen) return null;

  const isCentered = !position;

  const cardClassName = isCentered
    ? 'modal-card modal-card--sm'
    : styles.modal;
  const headerClassName = isCentered ? 'modal-header' : styles.header;
  const titleClassName = isCentered ? 'modal-title' : styles.title;
  const closeButtonClassName = isCentered ? 'modal-close-button' : styles.closeButton;
  const formClassName = isCentered ? `modal-body ${styles.formLayout}` : styles.form;

  const modalContent = (
    <div
      ref={modalRef}
      className={cardClassName}
      onClick={isCentered ? (e) => e.stopPropagation() : undefined}
      style={position ? {
        position: 'fixed',
        top: `${position.top}px`,
        left: `${position.left}px`,
      } : undefined}
    >
      {/* ヘッダーと閉じるボタン */}
      <div className={headerClassName}>
        <span className={titleClassName}>{brandConfig.labels.tag}を新規作成</span>
        <button
          type="button"
          onClick={onClose}
          className={closeButtonClassName}
          aria-label="閉じる"
        >
          ✕
        </button>
      </div>

      <form onSubmit={handleSubmit} className={formClassName}>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={`${brandConfig.labels.tag}名を入力`}
          className={styles.input}
          autoFocus
        />

        <div className={styles.colorPaletteField}>
          <span className={styles.colorPaletteLabel}>タグカラー</span>
          <div className={styles.colorPalette}>
            {TAG_COLOR_PALETTE.map((c) => (
              <button
                key={c.value}
                type="button"
                className={`${styles.colorSwatch} ${color === c.value ? styles.colorSwatchSelected : ''}`}
                style={{ backgroundColor: c.value }}
                onClick={() => setColor(c.value)}
                title={c.name}
                aria-label={c.name}
              />
            ))}
          </div>
        </div>

        {error && <div className={styles.error}>{error}</div>}

        <div className={isCentered ? 'modal-actions' : undefined}>
          <button
            type="submit"
            disabled={!name.trim() || createTagMutation.isPending}
            className="btn-modal btn-modal-accent"
          >
            {createTagMutation.isPending ? '保存中...' : '保存'}
          </button>
        </div>
      </form>
    </div>
  );

  return createPortal(
    isCentered ? (
      <div className="modal-overlay" onClick={onClose}>
        {modalContent}
      </div>
    ) : (
      modalContent
    ),
    document.body
  );
}
